import React, { useState } from "react";
import { Observer } from "mobx-react-lite";
import { Percent, Add, Delete } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";
import PaymentFeeTabProperties from "./PaymentFeeTabProperties";
import Access from "../../../Access/AllowAccess/Access";
import PaymentType from "../../../../entities/PaymentType";
import { useI18nContext } from "../../../../contexts/I18nContext";
import { useUserContext } from "../../../../contexts/UserContext";
import PaymentFee from "../../../../entities/PaymentFee";
import PriceInput from "../../../Inputs/PriceInput/PricetInput";
import { useStyles } from "./PaymentFeeTab.style";
import Checkbox from "../../../../ui_components/Checkbox/Checkbox";
import TextInput from "../../../../ui_components/TextInput/TextInput";
import TextInputType from "../../../../ui_components/TextInput/TextInputType";
import PaymentTypeStaticSelect from "../../../Selects/PaymentTypeStaticSelect/PaymentTypeStaticSelect";
import { generateId } from "../../../../util/Random";
import ButtonType from "../../../../ui_components/Button/ButtonType";
import Icon from "../../../../ui_components/Icon/Icon";
import IconName from "../../../../ui_components/Icon/enums/IconName";
import Button from "../../../../ui_components/Button/Button";

const PaymentFeeRow: React.FC<{ paymentFee: PaymentFee; onUpdate: (paymentFee: PaymentFee) => void, onRemove: () => void }> = ({ 
        paymentFee, 
        onUpdate, 
        onRemove 
    }) => {
        const classes = useStyles();
        const i18n = useI18nContext();
        const [isPercent, setIsPercent] = useState<boolean>(paymentFee.isPercentage);

        const onFromValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newFromValue = parseFloat(event.target.value) || 0;
            paymentFee.fromValue = newFromValue;
            onUpdate(paymentFee);
        };

        const onToValueChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
            const newToValue = parseFloat(event.target.value) || 0;
            paymentFee.toValue = newToValue;
            onUpdate(paymentFee);
        };

        const onValueChange = (newValue: number) => {
            paymentFee.value = newValue;
            onUpdate(paymentFee);
        };

        const onIsPercentChange = (newValue: boolean) => {
            paymentFee.isPercentage = newValue;
            setIsPercent(newValue);
            onUpdate(paymentFee);
        };

        const onPaymentTypeChange = (paymentType?: PaymentType) => {
            if (paymentType) {
                paymentFee.paymentType = paymentType;
            }
            onUpdate(paymentFee);
        };

        return (
            <div className={classes.payment_fee_row_container}>
                <div className={classes.payment_type}>
                    <PaymentTypeStaticSelect
                        selected={paymentFee.paymentType}
                        label={i18n.translate("PAYMENT_TYPE")}
                        onChange={(c) => onPaymentTypeChange(c)}
                    />
                </div>
                <div className={classes.range_container}>
                    <TextInput
                        type={TextInputType.NUMBER}
                        value={paymentFee.fromValue}
                        onChange={onFromValueChange}
                        label={i18n.translate("FROM")}
                        className={classes.range_input}
                        fullWidth
                    />
                    <TextInput
                        type={TextInputType.NUMBER}
                        maxNumberValue={1000000}
                        value={paymentFee.toValue}
                        onChange={onToValueChange}
                        label={i18n.translate("TO")}
                        className={classes.range_input}
                        fullWidth
                    />
                </div>
                <div className={classes.value_container}>
                    <PriceInput
                        onChange={onValueChange}
                        value={paymentFee.value}
                        icon={isPercent ? Percent : undefined}
                        label={isPercent ? 'Porciento' : 'Valor Fijo'}
                    />
                    <div className={classes.checkbox_row}>
                        <Checkbox
                            text={'Valor en %'}
                            onChange={onIsPercentChange}
                            selected={isPercent}
                        />
                    </div>
                </div>
                <IconButton
                    className={classes.remove_button}
                    onClick={onRemove}
                    size="small"
                    aria-label={i18n.translate("REMOVE")}
                >
                    <Delete />
                </IconButton>
            </div>
        );
    };

const PaymentFeeTab: React.FC<PaymentFeeTabProperties> = ({
    serviceName,
    agencyId,
    fees,
    onUpdate = () => {},
    onSave = () => {},
    onRemove = () => {},
    loading = false,
}) => {
    const classes = useStyles();
    const i18n = useI18nContext();
    const userStore = useUserContext();

    if (!userStore.hasAccess(Access.UPDATE_PAYMENT_FEES.valueOf())) {
        return <></>;
    }

    const handleAddFee = () => {
        const updatedFees = [
            {
                id: generateId(),
                serviceName,
                agencyId,
                fromValue: 0,
                toValue: 0,
                value: 0,
                isPercentage: false,
                paymentType: PaymentType.CASH,
            },
            ...fees
        ];

        onUpdate(updatedFees);
    };

    const handleRemoveFee = (feeId: string) => {
        if (onRemove) {
            onRemove(feeId);
        }
    };

    const save = async () => {
        if (onSave) {
            onSave();
        }
    };

    return (
        <Observer>
            {() => (
                <div className={classes.fees_container}>

                    <div className={classes.section_header}>
                        <span className={classes.section_title}>
                        </span>
                        <Button
                            onClick={handleAddFee}
                            type={ButtonType.PRIMARY}
                            text={'Adicionar'}
                            startIcon={<Icon name={IconName.CREATE_ICON}/>}
                        />
                    </div>

                    <div className={classes.fees_list}>
                        {fees.length === 0 ? (
                            <div className={classes.empty_state}>
                                <span className={classes.empty_state_text}>
                                    {'Sin tarifas para este servicio. Agrega tu primera tarifa.'}
                                </span>
                                <button
                                    className={classes.add_button}
                                    onClick={handleAddFee}
                                    type="button"
                                >
                                    <Add fontSize="small" />
                                    {'Agregar primera tarifa'}
                                </button>
                            </div>
                        ) : (
                            fees.map((fee) => (
                                <PaymentFeeRow
                                    key={fee.id}
                                    paymentFee={fee}
                                    onUpdate={(updatedFee) => {
                                        const index = fees.findIndex((f) => f.id === updatedFee.id);
                                        if (index !== -1) {
                                            fees[index] = updatedFee;
                                            onUpdate(fees);
                                        }
                                    }}
                                    onRemove={() => handleRemoveFee(fee.id)}
                                />
                            ))
                        )}
                    </div>

                    <div className={classes.actions_footer}>
                        <Button
                            onClick={save}
                            disabled={loading}
                            type={ButtonType.SECONDARY}
                            text={'Guardar'}
                        >
                            {loading ? i18n.translate("SAVING") : i18n.translate("SAVE")}
                        </Button>
                    </div>
                </div>
            )}
        </Observer>
    );
};

export default PaymentFeeTab;
